import { Command } from 'commander';
import { TaskOrchestrator } from '../core/task-orchestrator';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import pc from 'picocolors';
import inquirer from 'inquirer';

export function createOrchestrateCommand(): Command {
  const command = new Command('orchestrate');
  
  command
    .description('Task orchestration for Claude Code')
    .alias('orch');

  // Start orchestration
  command
    .command('start')
    .description('Start task orchestration')
    .option('--auto', 'Automatically assign next tasks', true)
    .option('--timeout <minutes>', 'Task timeout in minutes', '120')
    .action(withErrorHandling(async (options) => {
      console.log(pc.bold('\n🎯 Starting Task Orchestration\n'));
      
      const orchestrator = new TaskOrchestrator({
        autoAssignNext: options.auto,
        taskTimeout: parseInt(options.timeout)
      });
      
      await orchestrator.initialize();
      
      // 设置事件监听
      orchestrator.on('task-assigned', (instruction) => {
        console.log(pc.green(`\n✅ New task assigned: ${instruction.task.content}`));
        console.log(pc.dim(`Task ID: ${instruction.task.id}`));
        console.log(pc.dim(`Estimated time: ${instruction.context.timeEstimate || 60} minutes`));
      });
      
      orchestrator.on('task-completed', (instruction) => {
        console.log(pc.green(`\n🎉 Task completed: ${instruction.task.content}`));
      });
      
      orchestrator.on('task-timeout', (instruction) => {
        console.log(pc.yellow(`\n⏱️  Task timeout: ${instruction.task.content}`));
      });
      
      await orchestrator.start();
      
      console.log(pc.green('✅ Task orchestration started'));
      console.log(pc.dim('\nClaude Code will receive tasks automatically.'));
      console.log(pc.dim('Check .claude/current-task.md for current instructions.'));
      
      // Keep process running
      process.on('SIGINT', async () => {
        console.log('\nStopping orchestration...');
        await orchestrator.stop();
        process.exit(0);
      });
    }));

  // Stop orchestration
  command
    .command('stop')
    .description('Stop task orchestration')
    .action(withErrorHandling(async () => {
      const orchestrator = new TaskOrchestrator();
      await orchestrator.stop();
      console.log(pc.yellow('✋ Task orchestration stopped'));
    }));

  // Check progress (called by hook)
  command
    .command('check-progress')
    .description('Check task progress (called by hook)')
    .action(withErrorHandling(async () => {
      const orchestrator = new TaskOrchestrator();
      await orchestrator.initialize();
      await orchestrator.onCheckProgress();
    }));

  // Task completed (called by hook)
  command
    .command('task-completed')
    .description('Mark task as completed (called by hook)')
    .action(withErrorHandling(async () => {
      const orchestrator = new TaskOrchestrator();
      await orchestrator.initialize();
      await orchestrator.onTaskCompleted();
    }));

  // Session start (called by hook)
  command
    .command('session-start')
    .description('Handle session start (called by hook)')
    .action(withErrorHandling(async () => {
      const orchestrator = new TaskOrchestrator();
      await orchestrator.initialize();
      await orchestrator.onSessionStart();
    }));

  // Configure orchestration
  command
    .command('config')
    .description('Configure orchestration settings')
    .action(withErrorHandling(async () => {
      const config = await inquirer.prompt([
        {
          type: 'confirm',
          name: 'autoAssign',
          message: 'Automatically assign next tasks?',
          default: true
        },
        {
          type: 'number',
          name: 'taskTimeout',
          message: 'Task timeout in minutes:',
          default: 120
        },
        {
          type: 'list',
          name: 'priority',
          message: 'Task priority strategy:',
          choices: [
            { name: 'High priority first', value: 'high-first' },
            { name: 'Balanced', value: 'balanced' },
            { name: 'Easy tasks first', value: 'easy-first' }
          ],
          default: 'high-first'
        }
      ]);
      
      console.log(pc.green('✅ Orchestration configured'));
      console.log(pc.dim('Settings saved to .workflow/orchestrator-config.json'));
    }));

  // Show current task
  command
    .command('current')
    .description('Show current task instruction')
    .action(withErrorHandling(async () => {
      const fs = require('fs-extra');
      const path = require('path');
      
      const instructionFile = path.join(process.cwd(), '.claude/current-task.md');
      
      if (await fs.pathExists(instructionFile)) {
        const content = await fs.readFile(instructionFile, 'utf-8');
        console.log(pc.bold('\n📋 Current Task:\n'));
        console.log(content);
      } else {
        console.log(pc.yellow('No active task'));
      }
    }));

  return command;
}